/**
 * ConsolidationLabelButton — Sprint 5.3 (Feb 2026).
 *
 * Vervangt de eerdere <StubButton label="Label" SOON /> door een
 * werkende Preview + Download + Print knop voor het Consolidation
 * Label PDF endpoint ``GET /admin/consolidations/{cid}/label.pdf``.
 *
 * UI-pattern: één knop → dialog met inline preview (iframe) +
 * download- en printknoppen. Gebruikt bestaand ``useGlobalLoading``
 * runTask zodat double-click veilig is en errors netjes worden
 * afgevangen (spec §15).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Tag, Download, Printer, Loader2, X } from "lucide-react";
import { Button } from "../../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../ui/dialog";
import { adminApi } from "../../../context/AuthContext";
import { useGlobalLoading } from "../../../context/GlobalLoadingProvider";

export default function ConsolidationLabelButton({
  consolidationId, consolidationNumber,
}) {
  const [open, setOpen] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const { runTask } = useGlobalLoading();
  const cleanupRef = useRef(null);
  const iframeRef = useRef(null);

  const revokeBlob = useCallback(() => {
    if (cleanupRef.current) {
      try { URL.revokeObjectURL(cleanupRef.current); } catch { /* noop */ }
      cleanupRef.current = null;
    }
    setBlobUrl(null);
  }, []);

  useEffect(() => () => {
    // component-unmount cleanup — revoke without state update
    if (cleanupRef.current) {
      try { URL.revokeObjectURL(cleanupRef.current); } catch { /* noop */ }
      cleanupRef.current = null;
    }
  }, []);

  const fetchPdf = useCallback(async () => {
    if (!consolidationId) return null;
    const resp = await adminApi.get(
      `/admin/consolidations/${consolidationId}/label.pdf`,
      { responseType: "blob" },
    );
    return resp.data;
  }, [consolidationId]);

  const handleOpen = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await runTask({
        id: `cons-label-preview-${consolidationId}`,
        message: "Label genereren…",
        successMessage: null,
        run: async () => {
          const blob = await fetchPdf();
          const url = URL.createObjectURL(blob);
          // revoke previous blob (indien aanwezig) zonder setBlobUrl(null)
          // te forceren — voorkomt iframe-flash bij regenerate.
          if (cleanupRef.current) {
            try { URL.revokeObjectURL(cleanupRef.current); } catch { /* noop */ }
          }
          cleanupRef.current = url;
          setBlobUrl(url);
          setOpen(true);
        },
      });
    } catch (e) {
      if (e?.message === "DUPLICATE_TASK") return;
      if (e?.response?.status === 403) {
        toast.error("Geen toegang tot het consolidation label");
      } else if (e?.response?.status === 404) {
        toast.error("Consolidatie niet gevonden");
      } else if (e?.response?.status === 422) {
        toast.error(e?.response?.data?.detail
                     || "Consolidatie mist een geldig CON-nummer");
      } else {
        toast.error(e?.response?.data?.detail
                     || e?.message
                     || "Label kon niet worden gegenereerd");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      await runTask({
        id: `cons-label-download-${consolidationId}`,
        message: "Label downloaden…",
        successMessage: "Label gedownload",
        run: async () => {
          const blob = blobUrl
            ? await (await fetch(blobUrl)).blob()
            : await fetchPdf();
          const url = blobUrl || URL.createObjectURL(blob);
          const safe = (consolidationNumber || "consolidation")
            .replace(/[^a-zA-Z0-9_-]/g, "-");
          const a = document.createElement("a");
          a.href = url;
          a.download = `5999Cargo-${safe}-Consolidation-Label.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          if (!blobUrl) URL.revokeObjectURL(url);
        },
      });
    } catch (e) {
      if (e?.message === "DUPLICATE_TASK") return;
      toast.error(e?.response?.data?.detail
                   || e?.message
                   || "Download mislukt");
    }
  };

  const handlePrint = () => {
    // Document Integrity Sprint (Jun 2026): geen `window.open(blobUrl)` meer.
    // Chrome blokkeert top-level navigatie naar een blob-URL die na een async
    // fetch wordt geopend → dat leverde een leeg tabblad op. Printen gebeurt
    // nu via het reeds geladen preview-iframe.
    const frame = iframeRef.current;
    if (!frame || !blobUrl) {
      toast.error("Label nog niet geladen — wacht op de preview");
      return;
    }
    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } catch {
      toast.error("Printen geblokkeerd door de browser — gebruik Downloaden");
    }
  };

  return (
    <>
      <Button
        size="sm"
        onClick={handleOpen}
        variant="outline"
        disabled={loading || !consolidationId}
        className="rounded-none text-xs font-bold uppercase border-[#082c50] text-[#082c50] hover:bg-[#082c50] hover:text-white"
        data-testid="consolidation-action-label"
        title="Consolidation label genereren"
      >
        {loading
          ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
          : <Tag className="w-3 h-3 mr-1" />}
        Label
      </Button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) revokeBlob(); }}>
        <DialogContent className="max-w-3xl" data-testid="consolidation-label-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-[#082c50]" />
              Consolidation Label
              {consolidationNumber && (
                <span className="font-mono text-[#0055FF]">
                  {consolidationNumber}
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              A6 label met CON-nummer, transportmode, route, planning en totals.
              Gebruik Downloaden of Printen om te verwerken.
            </DialogDescription>
          </DialogHeader>

          <div className="border border-gray-200 bg-slate-50 overflow-hidden" style={{ height: 520 }}>
            {blobUrl ? (
              <iframe
                ref={iframeRef}
                title="consolidation-label-preview"
                src={blobUrl}
                width="100%" height="100%"
                style={{ border: 0 }}
                data-testid="consolidation-label-iframe"
              />
            ) : (
              <div className="flex items-center justify-center h-full text-slate-500 text-sm">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Label laden…
              </div>
            )}
          </div>

          <DialogFooter className="flex-row justify-between">
            <Button
              variant="outline" size="sm"
              onClick={() => { setOpen(false); revokeBlob(); }}
              className="rounded-none text-xs font-bold uppercase"
              data-testid="consolidation-label-close"
            >
              <X className="w-3 h-3 mr-1" /> Sluiten
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm"
                onClick={handlePrint}
                disabled={!blobUrl}
                className="rounded-none text-xs font-bold uppercase border-[#082c50] text-[#082c50] hover:bg-[#082c50] hover:text-white"
                data-testid="consolidation-label-print"
              >
                <Printer className="w-3 h-3 mr-1" /> Printen
              </Button>
              <Button
                size="sm"
                onClick={handleDownload}
                disabled={!blobUrl}
                className="rounded-none text-xs font-bold uppercase bg-[#0055FF] text-white hover:bg-[#003fcc]"
                data-testid="consolidation-label-download"
              >
                <Download className="w-3 h-3 mr-1" /> Downloaden
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
